import { debounce } from 'lodash';
import React, {
  ComponentType,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { setDisplayName, wrapDisplayName } from 'recompose';
import { FieldValueType } from 'api/tasks';
import { ColumnsProps } from 'components/atoms/grid';
import { TaskField } from 'stores/fields';

type OnChange = (
  record: Record<string, unknown>,
  field: TaskField,
  from: FieldValueType,
  to: FieldValueType,
  callback?: VoidFunction,
) => void;

export type OnChangeProps = {
  onChange?: OnChange;
};

export const withOnChange = <T extends ColumnsProps>(
  Component: ComponentType<T>,
): FC<T & OnChangeProps> => {
  const Next: FC<T & OnChangeProps> = ({
    onChange: actualOnChange,
    columns: actualColumns,
    ...props
  }) => {
    const onChangeRef = useRef<OnChange | null>(actualOnChange ?? null);

    useEffect(() => {
      onChangeRef.current =
        actualOnChange == null ? null : debounce(actualOnChange, 300);
    }, [onChangeRef, actualOnChange]);

    const onChange = useCallback(
      (
        record: Record<string, unknown>,
        field: TaskField,
        from: FieldValueType,
        to: FieldValueType,
        callback?: VoidFunction,
      ) => {
        onChangeRef.current?.(record, field, from, to, callback);
      },
      [onChangeRef],
    );
    const columns = useMemo(
      () =>
        actualColumns.map((column) => ({
          ...column,
          editorParams: {
            ...column.editorParams,
            onUpdate: onChange,
          },
        })),
      [actualColumns, onChange],
    );

    return <Component {...(props as any)} columns={columns} />;
  };

  return setDisplayName(wrapDisplayName(Component, 'WithOnChange'))(Next);
};
